import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Copy, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharePatientDialog = ({ open, onOpenChange, patient }) => {
  const [email, setEmail] = useState('');
  const [permissions, setPermissions] = useState('view');
  const [dataToShare, setDataToShare] = useState({
    symptoms: true,
    medications: true,
    appointments: true,
    notes: true,
    blood_pressure: true,
    blood_sugar: true,
    critical_events: true,
  });
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGenerateLink = async () => {
    if (!patient) return;

    // Validate at least one data type is selected
    const selectedData = Object.entries(dataToShare)
      .filter(([_, selected]) => selected)
      .map(([key, _]) => key);

    if (selectedData.length === 0) {
      toast.error('Please select at least one data type to share');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/patients/${patient.id}/invitations`,
        {
          invited_email: email || null,
          permissions,
          data_to_share: selectedData,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const fullLink = `${window.location.origin}${response.data.share_link}`;
      setShareLink(fullLink);
      toast.success('Shareable link generated!');
    } catch (error) {
      console.error('Error generating link:', error);
      toast.error('Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setEmail('');
    setShareLink('');
    setCopied(false);
    setPermissions('view');
    setDataToShare({
      symptoms: true,
      medications: true,
      appointments: true,
      notes: true,
      blood_pressure: true,
      blood_sugar: true,
      critical_events: true,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share Patient Profile</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 px-2">
          {/* Patient Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm font-medium" style={{ color: '#A2DAD3' }}>
              Sharing: {patient?.name}
            </p>
          </div>

          {/* Optional Email */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Recipient Email (Optional)
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="input pl-10"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">For your reference only</p>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium mb-3">Access Level</label>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="view"
                  name="permissions"
                  checked={permissions === 'view'}
                  onChange={() => setPermissions('view')}
                  className="w-4 h-4"
                />
                <label htmlFor="view" className="text-sm cursor-pointer flex-1">
                  <strong>View Only</strong> - Can view selected data
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="edit"
                  name="permissions"
                  checked={permissions === 'edit'}
                  onChange={() => setPermissions('edit')}
                  className="w-4 h-4"
                />
                <label htmlFor="edit" className="text-sm cursor-pointer flex-1">
                  <strong>Can Edit</strong> - Can view and add/update data
                </label>
              </div>
            </div>
          </div>

          {/* Data to Share */}
          <div>
            <label className="block text-sm font-medium mb-3">Data to Share</label>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="symptoms"
                  checked={dataToShare.symptoms}
                  onCheckedChange={(checked) =>
                    setDataToShare({ ...dataToShare, symptoms: checked })
                  }
                />
                <label htmlFor="symptoms" className="text-sm cursor-pointer flex-1">
                  Symptoms
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="medications"
                  checked={dataToShare.medications}
                  onCheckedChange={(checked) =>
                    setDataToShare({ ...dataToShare, medications: checked })
                  }
                />
                <label htmlFor="medications" className="text-sm cursor-pointer flex-1">
                  Medications
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="appointments"
                  checked={dataToShare.appointments}
                  onCheckedChange={(checked) =>
                    setDataToShare({ ...dataToShare, appointments: checked })
                  }
                />
                <label htmlFor="appointments" className="text-sm cursor-pointer flex-1">
                  Appointments
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="notes"
                  checked={dataToShare.notes}
                  onCheckedChange={(checked) =>
                    setDataToShare({ ...dataToShare, notes: checked })
                  }
                />
                <label htmlFor="notes" className="text-sm cursor-pointer flex-1">
                  Notes
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="blood_pressure"
                  checked={dataToShare.blood_pressure}
                  onCheckedChange={(checked) =>
                    setDataToShare({ ...dataToShare, blood_pressure: checked })
                  }
                />
                <label htmlFor="blood_pressure" className="text-sm cursor-pointer flex-1">
                  Blood Pressure
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="blood_sugar"
                  checked={dataToShare.blood_sugar}
                  onCheckedChange={(checked) =>
                    setDataToShare({ ...dataToShare, blood_sugar: checked })
                  }
                />
                <label htmlFor="blood_sugar" className="text-sm cursor-pointer flex-1">
                  Blood Sugar
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="critical_events"
                  checked={dataToShare.critical_events}
                  onCheckedChange={(checked) =>
                    setDataToShare({ ...dataToShare, critical_events: checked })
                  }
                />
                <label htmlFor="critical_events" className="text-sm cursor-pointer flex-1">
                  Critical Events
                </label>
              </div>
            </div>
          </div>

          {/* Generate Link Button */}
          {!shareLink && (
            <button
              onClick={handleGenerateLink}
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? 'Generating...' : 'Generate Share Link'}
            </button>
          )}

          {/* Share Link Display */}
          {shareLink && (
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 mb-2">Shareable Link:</p>
                <p className="text-sm break-all font-mono">{shareLink}</p>
              </div>
              <button
                onClick={handleCopyLink}
                className="btn btn-coral w-full flex items-center justify-center gap-2"
              >
                {copied ? (
                  <>
                    <Check size={16} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Copy Link
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 text-center">
                Link expires in 7 days
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SharePatientDialog;
